import { ACCOUNT_ELIGIBILITY_POLICY } from "../config/account-eligibility-policy.js";

export const SECOND_LIFE_AVATAR_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const SECOND_LIFE_NULL_KEY = "00000000-0000-0000-0000-000000000000";
export const ACCOUNT_AGE_REFERENCE_TIME_ZONE = "America/New_York";

export function normalizeEmail(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function isValidSignupEmail(value) {
  const original = String(value ?? "").trim();
  if (!original || original.length > 254 || /\s/u.test(original)) return false;
  const firstAt = original.indexOf("@");
  if (firstAt <= 0 || firstAt !== original.lastIndexOf("@") || firstAt === original.length - 1) return false;
  const local = original.slice(0, firstAt);
  const domain = original.slice(firstAt + 1);
  return local.length <= 64
    && /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/u.test(local)
    && !local.startsWith(".")
    && !local.endsWith(".")
    && !local.includes("..")
    && /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/iu.test(domain);
}

export function normalizeUsername(value) {
  return String(value ?? "")
    .trim()
    .replace(/[A-Z]/g, (letter) => String.fromCharCode(letter.charCodeAt(0) + 32));
}

export function normalizePublicIdentityForPolicy(value) {
  return String(value ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]/gu, "");
}

export function publicIdentityPolicyError(
  value,
  {
    fieldId,
    policy = ACCOUNT_ELIGIBILITY_POLICY,
  } = {},
) {
  if (!policy?.publicIdentity?.coveredFields?.includes(fieldId)) return "";
  const normalized = normalizePublicIdentityForPolicy(value);
  if (!normalized) return "";

  const containsReservedBrand = policy.publicIdentity.reservedBrandTokens
    .some((token) => normalized.includes(normalizePublicIdentityForPolicy(token)));
  if (!containsReservedBrand) return "";

  const containsBlockedTerm = policy.publicIdentity.abusiveBrandedNames.blockedTerms
    .some((term) => normalized.includes(normalizePublicIdentityForPolicy(term)));
  return containsBlockedTerm
    ? policy.publicIdentity.abusiveBrandedNames.message
    : policy.publicIdentity.reservedBrandMessage;
}

export function emailProviderPolicyError(value, policy = ACCOUNT_ELIGIBILITY_POLICY) {
  const candidate = String(value ?? "").trim();
  if (!isValidSignupEmail(candidate)) return "Enter a valid email address.";
  const match = /^([^\s@]+)@([^\s@]+)$/u.exec(candidate);
  const domain = match[2].toLowerCase();
  return policy?.email?.allowedDomains?.includes(domain)
    ? ""
    : policy.email.unsupportedProviderMessage;
}

export function normalizeSecondLifeAvatarKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function isValidSecondLifeAvatarKey(value) {
  const candidate = normalizeSecondLifeAvatarKey(value);
  return candidate !== SECOND_LIFE_NULL_KEY && SECOND_LIFE_AVATAR_KEY_PATTERN.test(candidate);
}

export function passwordPolicyError(value) {
  const password = String(value ?? "");
  if (password.length < 15) return "Use at least 15 characters.";
  const letters = password.match(/\p{Letter}/gu) || [];
  if (letters.length < 2) return "Use letters, including at least two different letters.";
  const distinctLetters = new Set(letters.map((letter) => letter.normalize("NFKC").toLowerCase()));
  if (distinctLetters.size < 2) return "Do not repeat only one letter; use at least two different letters.";
  return "";
}

function daysInMonth(year, month) {
  if (month === 2) {
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return leapYear ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function parseISOCalendarDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? "").trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function referenceCalendarDate(referenceDate = new Date()) {
  if (referenceDate instanceof Date) {
    if (Number.isNaN(referenceDate.getTime())) throw new TypeError("A valid reference date is required.");
    const dateParts = new Intl.DateTimeFormat("en-CA-u-ca-gregory-nu-latn", {
      timeZone: ACCOUNT_AGE_REFERENCE_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(referenceDate);
    const values = Object.fromEntries(dateParts.map(({ type, value }) => [type, value]));
    return { year: Number(values.year), month: Number(values.month), day: Number(values.day) };
  }
  const parts = typeof referenceDate === "string" ? parseISOCalendarDate(referenceDate) : referenceDate;
  if (
    !parts
    || !Number.isInteger(parts.year)
    || !Number.isInteger(parts.month)
    || !Number.isInteger(parts.day)
    || parts.month < 1
    || parts.month > 12
    || parts.day < 1
    || parts.day > daysInMonth(parts.year, parts.month)
  ) throw new TypeError("A valid reference calendar date is required.");
  return { year: parts.year, month: parts.month, day: parts.day };
}

function calendarDateISO({ year, month, day }) {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function compareCalendarDates(left, right) {
  return calendarDateISO(left).localeCompare(calendarDateISO(right));
}

export function latestEligibleBirthDateISO(minimumAge = 18, referenceDate = new Date()) {
  if (!Number.isInteger(minimumAge) || minimumAge < 1 || minimumAge > 150) {
    throw new RangeError("minimumAge must be an integer from 1 through 150.");
  }
  const today = referenceCalendarDate(referenceDate);
  const year = today.year - minimumAge;
  const day = Math.min(today.day, daysInMonth(year, today.month));
  return calendarDateISO({ year, month: today.month, day });
}

export function earliestSupportedBirthDateISO(
  maximumAgeExclusive = ACCOUNT_ELIGIBILITY_POLICY.age.maximumAgeExclusive,
  referenceDate = new Date(),
) {
  if (!Number.isInteger(maximumAgeExclusive) || maximumAgeExclusive < 1 || maximumAgeExclusive > 150) {
    throw new RangeError("maximumAgeExclusive must be an integer from 1 through 150.");
  }
  const today = referenceCalendarDate(referenceDate);
  const year = today.year - maximumAgeExclusive;
  const day = Math.min(today.day, daysInMonth(year, today.month));
  const boundary = { year, month: today.month, day };
  boundary.day += 1;
  if (boundary.day > daysInMonth(boundary.year, boundary.month)) {
    boundary.day = 1;
    boundary.month += 1;
    if (boundary.month > 12) {
      boundary.month = 1;
      boundary.year += 1;
    }
  }
  return calendarDateISO(boundary);
}

export function birthDatePolicyError(
  value,
  {
    minimumAge = ACCOUNT_ELIGIBILITY_POLICY.age.minimumAge,
    maximumAgeExclusive = ACCOUNT_ELIGIBILITY_POLICY.age.maximumAgeExclusive,
    referenceDate = new Date(),
  } = {},
) {
  const birthDate = parseISOCalendarDate(value);
  if (!birthDate) return "Enter a valid date of birth.";
  const today = referenceCalendarDate(referenceDate);
  if (compareCalendarDates(birthDate, today) > 0) return "Date of birth cannot be in the future.";
  const cutoff = parseISOCalendarDate(latestEligibleBirthDateISO(minimumAge, today));
  if (compareCalendarDates(birthDate, cutoff) > 0) return `You must be at least ${minimumAge} years old.`;
  const earliest = parseISOCalendarDate(earliestSupportedBirthDateISO(maximumAgeExclusive, today));
  if (compareCalendarDates(birthDate, earliest) < 0) return ACCOUNT_ELIGIBILITY_POLICY.age.outOfRangeMessage;
  return "";
}
